import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { IBetBuilderCombinationDisplayRule, ISingleMatchBetBuilderCombinationDisplayRule } from '@/interface/betBuilder';
import { RecommendType, SportType } from '@/interface/enum';
import type {
    RogueModelsRequestsBetBuilderPreBuiltForMatchRequest,
    RogueModelsRequestsBetBuilderPreBuiltRequest,
    RogueModelsRequestsPopularMixParlayRequest,
    RogueModelsResponsesBetBuilderPreBuiltResponse,
    RogueModelsResponsesPopularMixParlayResponse,
} from '@/interface/rogue';
import { createApiInstance } from '@/libraries/apis/createApiInstance';
import {
    formatBetBuilderRecommendations,
} from '@/libraries/recommendation/betBuilderRecommendationHelper';
import { formatMpRecommendation } from '@/libraries/recommendation/mpRecommendationHelper';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

function createInstance(baseUrl: string) {
    return createApiInstance({
        baseURL: baseUrl,
        timeout: 15000,
        withCredentials: true,
    });
}

function getRecommendationBaseUrl(): string {
    return useFeatureToggleStore().newOddsApiUrl ?? '';
}

const notDefinedType: unique symbol = Symbol('nonExist');
type NotDefinedType = typeof notDefinedType

type TypedParamsAxiosRequestConfig<TParams> = Omit<AxiosRequestConfig, 'params'> & { params?: TParams }

/** wrapper for `axios.get` with request & response type checking */
function get<TRequest = NotDefinedType, TResponse = NotDefinedType>(
    url: string,
    config?: TypedParamsAxiosRequestConfig<TRequest extends NotDefinedType ? 'You must provide type parameters' : NoInfer<TRequest>>,
): Promise<AxiosResponse<TResponse>> {
    return createInstance(getRecommendationBaseUrl()).get<TResponse>(url, config);
}

export interface BetBuilderRecommendationsParams {
    isLivePage?: boolean;
    preBuiltSelectionDisplay?: IBetBuilderCombinationDisplayRule[];
}

function computeSelectionHash(displayRule: IBetBuilderCombinationDisplayRule[] | ISingleMatchBetBuilderCombinationDisplayRule): string {
    return btoa(JSON.stringify(displayRule))
        .replaceAll('+', '-')
        .replaceAll('/', '_')
        .replaceAll('=', '');
}

export const RecommendationApi = {
    async getMpRecommendations(sportType: SportType, recommendType: RecommendType, count: number) {
        const response = await get<RogueModelsRequestsPopularMixParlayRequest, RogueModelsResponsesPopularMixParlayResponse>('/popularMixParlay', {
            params: {
                sportType: SportType.toRogue(sportType),
                recommendType: RecommendType.toRogue(recommendType),
                count,
            },
        });
        return formatMpRecommendation(response.data);
    },

    async getBetBuilderRecommendations(params: BetBuilderRecommendationsParams = {}) {
        const { isLivePage, preBuiltSelectionDisplay = [] } = params;
        const selectionHash = computeSelectionHash(preBuiltSelectionDisplay);
        const response = await get<RogueModelsRequestsBetBuilderPreBuiltRequest, RogueModelsResponsesBetBuilderPreBuiltResponse>(`/betBuilderPreBuilt/${selectionHash}`, {
            params: { isLivePage },
        });
        return formatBetBuilderRecommendations(response.data);
    },

    async getBetBuilderRecommendationsByMatch(matchId: number, displayRule: ISingleMatchBetBuilderCombinationDisplayRule) {
        const selectionHash = computeSelectionHash(displayRule);
        const response = await get<RogueModelsRequestsBetBuilderPreBuiltForMatchRequest, RogueModelsResponsesBetBuilderPreBuiltResponse>(`/betBuilderPreBuilt/${matchId}/${selectionHash}`);
        return formatBetBuilderRecommendations(response.data);
    },
};
